export const pad2 = (n) => String(n).padStart(2, "0")

// YYYY-MM-DD (for API query)
export const toISODate = (d) => {
  if (!d) return ""
  const dt = new Date(d)
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`
}

// MM/DD/YYYY (to match API response "date": "01/31/2026")
export const toMMDDYYYY = (d) => {
  if (!d) return ""
  const dt = new Date(d)
  return `${pad2(dt.getMonth() + 1)}/${pad2(dt.getDate())}/${dt.getFullYear()}`
}

// "HH:MM" or "HH:MM:SS" -> minutes
export const hhmmToMinutes = (t) => {
  const parts = String(t || "").split(":")
  const h = Number(parts[0] || 0)
  const m = Number(parts[1] || 0)
  return h * 60 + m
}

export const parseRanges = (times = []) =>
  (times || [])
    .flatMap((range) => {
      const [start, end] = String(range).split("-")
      if (!start || !end) return []

      const startMin = hhmmToMinutes(start)
      const endMin = hhmmToMinutes(end)

      if (endMin >= startMin) return [{ startMin, endMin }]

      return [
        { startMin, endMin: 24 * 60 - 1 },
        { startMin: 0, endMin },
      ]
    })

export const isInBlockedRange = (minuteValue, ranges) =>
  (ranges || []).some(
    ({ startMin, endMin }) => minuteValue >= startMin && minuteValue <= endMin,
  )

export const isRangeOverlappingBlockedRange = (
  startMin,
  durationMinutes = 0,
  ranges = [],
) => {
  const duration = Math.max(0, Number(durationMinutes) || 0)
  const endMin = startMin + duration

  return (ranges || []).some(({ startMin: blockedStart, endMin: blockedEnd }) =>
    duration > 0
      ? startMin < blockedEnd + 1 && blockedStart < endMin
      : startMin >= blockedStart && startMin <= blockedEnd,
  )
}

export const toNonNegativeMinutes = (value, fallback = 0) => {
  const num = Number(value)
  return Number.isFinite(num) && num >= 0 ? num : fallback
}

export const getBufferedDayRange = (
  dayHours,
  openingBuffer = 0,
  closingBuffer = 0,
) => {
  if (!dayHours?.is_open || !dayHours?.open || !dayHours?.close) return null

  const openRaw = hhmmToMinutes(dayHours.open)
  let closeRaw = hhmmToMinutes(dayHours.close)

  if (closeRaw <= openRaw) closeRaw += 24 * 60

  const startMin = openRaw + toNonNegativeMinutes(openingBuffer)
  const endMin = closeRaw - toNonNegativeMinutes(closingBuffer)

  if (endMin <= startMin) return null

  return { startMin, endMin }
}

export const getBufferedDayWindow = (
  dayHours,
  openingBuffer = 0,
  closingBuffer = 0,
) => {
  const range = getBufferedDayRange(dayHours, openingBuffer, closingBuffer)
  if (!range) return null

  return {
    startMin: Math.max(0, range.startMin),
    endMin: Math.min(24 * 60, range.endMin),
  }
}

export const getCalendarDayBookableWindows = (
  operatingHours,
  selectedDate,
  openingBuffer = 0,
  closingBuffer = 0,
) => {
  if (!operatingHours || !selectedDate) return []

  const targetDate = new Date(selectedDate)
  if (Number.isNaN(targetDate.getTime())) return []

  const weekday = targetDate
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase()
  const windows = []

  const dayHours = operatingHours?.[weekday]
  const dayRange = getBufferedDayRange(dayHours, openingBuffer, closingBuffer)
  if (dayRange) {
    const startMin = Math.max(0, dayRange.startMin)
    const endMin = dayRange.endMin
    if (endMin > startMin) windows.push({ startMin, endMin })
  }

  const prevDate = new Date(targetDate)
  prevDate.setDate(targetDate.getDate() - 1)
  const prevWeekday = prevDate
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase()
  const prevDayHours = operatingHours?.[prevWeekday]
  const prevRange = getBufferedDayRange(
    prevDayHours,
    openingBuffer,
    closingBuffer,
  )
  if (prevRange && prevRange.endMin > 24 * 60) {
    const endMin = Math.min(prevRange.endMin - 24 * 60, 24 * 60)
    if (endMin > 0) windows.push({ startMin: 0, endMin })
  }

  return windows.sort((a, b) => a.startMin - b.startMin)
}

export const buildTimeOptionsFromWindow = (
  dayWindow,
  intervalMinutes = 30,
  durationMinutes = 0,
) => {
  if (!dayWindow) return []

  const safeInterval = Math.max(1, Number(intervalMinutes) || 30)
  const safeDuration = Math.max(0, Number(durationMinutes) || 0)
  const latestStartMin = safeDuration
    ? dayWindow.endMin - safeDuration
    : dayWindow.endMin - safeInterval
  const options = []

  for (
    let totalMinutes = dayWindow.startMin;
    totalMinutes <= latestStartMin && totalMinutes < 24 * 60;
    totalMinutes += safeInterval
  ) {
    const hours24 = Math.floor(totalMinutes / 60) % 24
    const minutes = totalMinutes % 60

    const period = hours24 >= 12 ? "PM" : "AM"
    const hours12 = ((hours24 + 11) % 12) + 1

    options.push({
      label: `${hours12}:${pad2(minutes)} ${period}`,
      value: `${pad2(hours24)}:${pad2(minutes)}`,
    })
  }

  return options
}

export const buildTimeOptionsFromWindows = (
  dayWindows = [],
  intervalMinutes = 30,
  durationMinutes = 0,
) => {
  if (!Array.isArray(dayWindows) || dayWindows.length === 0) return []

  const seen = new Set()
  const options = []

  dayWindows.forEach((window) => {
    buildTimeOptionsFromWindow(window, intervalMinutes, durationMinutes).forEach(
      (option) => {
        if (!seen.has(option.value)) {
          seen.add(option.value)
          options.push(option)
        }
      },
    )
  })

  return options
}

export const expandRangesByBuffer = (ranges = [], bufferMinutes = 0) => {
  const buffer = toNonNegativeMinutes(bufferMinutes)
  if (!buffer) return ranges

  return (ranges || []).map(({ startMin, endMin }) => ({
    startMin: Math.max(0, startMin - buffer),
    endMin: endMin + buffer,
  }))
}

export const buildBaseTimeOptions = () =>
  Array.from({ length: 48 }, (_, i) => {
    const totalMinutes = i * 30
    const hours24 = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    const period = hours24 >= 12 ? "PM" : "AM"
    const hours12 = ((hours24 + 11) % 12) + 1
    const label = `${hours12}:${pad2(minutes)} ${period}`

    const value = `${pad2(hours24)}:${pad2(minutes)}`
    return { label, value }
  })

// Filters out any options whose time falls within a blocked range.
export const filterTimeOptionsByRanges = (baseOptions, ranges) => {
  if (!ranges?.length) return baseOptions
  return baseOptions.filter((opt) => {
    const optMin = hhmmToMinutes(opt.value)
    return !isInBlockedRange(optMin, ranges)
  })
}
